import type { FC, ReactNode } from "react";

import { noop } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import { Type, ChipItemTableCell } from "@similarweb/ui-components";

import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import { UpgradeLink } from "app/components/React/Table/cells/UpgradeLink";
import { WebsiteTooltip } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";

import type { Filter } from "../../../BoxSubtitle/src/BoxSubtitle";
import BoxSubtitle from "../../../BoxSubtitle/src/BoxSubtitle";
import { IconType, SegmentIcon, StyledItemIcon } from "../CoreRecentCell/StyledComponents";

import {
    Container,
    ContentContainer,
    ExternalLink,
    GAIconContainer,
    InnerLinkContainer,
    Link,
    Subtitle,
    TextContainer,
    SegmentTypeBadgeHorizontal,
    ChipItemTableCellWrapper,
    ChildrenCountContainer,
} from "./StyledComponents";

interface ICoreWebsiteCell {
    domain: string;
    icon: string;
    subtitleFilters?: Filter[] | unknown;
    internalLink?: string;
    externalLink?: string;
    trackInternalLink?: (e: any) => void;
    trackExternalLink?: (e: any) => void;
    hideTrackButton?: boolean;
    target?: string;
    className?: string;
    showGaIcon?: boolean;
    isGaPrivate?: boolean;
    withWebsiteSign?: boolean;
    textAndIconMargin?: string;
    showToolTip?: boolean;
    domainBadge?: string;
    childrenCount?: number;
    handleOpenQualificationSideBar?: VoidFunction;
    tag?: string;
    internalLinkDataAutomationAttribute?: string;
    spacingDomainAndAddons?: boolean;
    tooltipClassName?: string;
    TooltipComponent?: FC<{ children: ReactNode }>;
    showChipActionButton?: boolean;
    onChipClick?: (domain: string) => void;
    chipTooltip?: string;
    isTagDisabled?: boolean;
}

export const CoreWebsiteCell: FC<ICoreWebsiteCell> = ({
    domain,
    icon,
    subtitleFilters,
    internalLink,
    externalLink,
    trackInternalLink = noop,
    trackExternalLink = noop,
    hideTrackButton,
    target = "_self",
    className,
    showGaIcon,
    isGaPrivate,
    withWebsiteSign = false,
    textAndIconMargin = "15px",
    showToolTip = true,
    domainBadge,
    childrenCount,
    handleOpenQualificationSideBar,
    tag,
    internalLinkDataAutomationAttribute = "core-website-cell-internal-link",
    spacingDomainAndAddons,
    tooltipClassName,
    TooltipComponent,
    showChipActionButton,
    onChipClick,
    chipTooltip,
    isTagDisabled,
}) => {
    const isDisabled = domain === "grid.upgrade";
    let content;

    const linkContent = (
        <Link
            data-automation={internalLinkDataAutomationAttribute}
            target={target}
            href={internalLink}
            onClick={trackInternalLink}
        >
            <TextContainer
                className="core-website-cell--text-container"
                marginLeft={textAndIconMargin}
                spacingDomainAndAddons={spacingDomainAndAddons}
            >
                {domain}
            </TextContainer>
        </Link>
    );

    const tooltippedLink = TooltipComponent ? (
        <TooltipComponent>{linkContent}</TooltipComponent>
    ) : (
        <WebsiteTooltip
            domain={domain}
            mainDomainOnly={false}
            hideTrackButton={hideTrackButton}
            handleOpenQualificationSideBar={handleOpenQualificationSideBar ? handleOpenQualificationSideBar : undefined}
            className={tooltipClassName}
        >
            {linkContent}
        </WebsiteTooltip>
    );

    // Show an upgrade link in place of the cell
    if (isDisabled) {
        content = (
            <InnerLinkContainer alignItems="center">
                <UpgradeLink />
            </InnerLinkContainer>
        );
    } else {
        content = (
            <InnerLinkContainer alignItems="center">
                {showToolTip ? (
                    tooltippedLink
                ) : (
                    <TextContainer
                        marginLeft={textAndIconMargin}
                        spacingDomainAndAddons={spacingDomainAndAddons}
                    >
                        {domain}
                    </TextContainer>
                )}
                {(childrenCount ?? 0) > 0 ? <ChildrenCountContainer>({childrenCount})</ChildrenCountContainer> : null}
                {domainBadge && !withWebsiteSign ? (
                    <SegmentTypeBadgeHorizontal className="core-website-cell--domain-badge">
                        {domainBadge}
                    </SegmentTypeBadgeHorizontal>
                ) : null}
                {showGaIcon ? (
                    <GAIconContainer>
                        <GAVerifiedContainer
                            isActive={true}
                            // @ts-ignore FIXME: strict migration - Type 'boolean | undefined' is not assignable to type 'boolean' — GAVerifiedContainer.isPrivate is required boolean, isGaPrivate is optional here
                            isPrivate={isGaPrivate}
                            metric="visits"
                            tooltipIsActive={false}
                            tooltipAvailable={true}
                            size="SMALL"
                        />
                    </GAIconContainer>
                ) : null}
                {externalLink ? (
                    <ExternalLink
                        className={"link-out"}
                        href={externalLink}
                        target="_blank"
                        onClick={trackExternalLink}
                    >
                        <SWReactIcons iconName="link-out" />
                    </ExternalLink>
                ) : null}
                {tag ? (
                    <ChipItemTableCellWrapper>
                        <ChipItemTableCell
                            text={tag}
                            onCloseChip={() => onChipClick?.(domain)}
                            hideActionButton={!showChipActionButton}
                            tooltipText={chipTooltip}
                            displayTooltipForTextLongerThan={isTagDisabled ? 0 : undefined}
                            isDisabled={isTagDisabled}
                        />
                    </ChipItemTableCellWrapper>
                ) : null}
            </InnerLinkContainer>
        );
    }

    return (
        <Container className={className}>
            {!isDisabled ? (
                <>
                    <StyledItemIcon
                        iconType={Type.Website}
                        iconSrc={icon}
                    />
                    {withWebsiteSign ? (
                        <IconType>
                            <SegmentIcon iconName="website-analysis" />
                        </IconType>
                    ) : null}
                </>
            ) : null}

            <ContentContainer>
                {content}
                {subtitleFilters ? (
                    <Subtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </Subtitle>
                ) : null}
            </ContentContainer>
        </Container>
    );
};
